import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/config/supabase";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import apiService from "@/lib/api-functions/api-service";

interface UseAuthTokenHandlerProps {
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  setConfirmationMessage: (message: string) => void;
}

/**
 * Helper to exchange Supabase token for custom JWT.
 * The Supabase token cannot be used with our API endpoints directly
 * because they expect JWTs signed with our JWT_SECRET.
 */
async function exchangeSupabaseTokenForCustomJWT(supabaseToken: string): Promise<boolean> {
  try {
    console.log('🔄 Exchanging Supabase token for custom JWT...');
    const exchangeResult = await apiService.exchangeSession(supabaseToken);
    if (exchangeResult.success && exchangeResult.data?.token) {
      apiService.storeToken(exchangeResult.data.token);
      console.log('✅ Successfully exchanged Supabase token for custom JWT');
      return true;
    } else {
      console.error('❌ Failed to exchange session:', exchangeResult.error);
      return false;
    }
  } catch (err) {
    console.error('❌ Error exchanging session token:', err);
    return false;
  }
}

/**
 * Hook to handle email confirmation tokens, magic link tokens, and invitation tokens.
 * This handles both hash-based tokens (Supabase implicit flow) and query param-based tokens (PKCE flow).
 */
export function useAuthTokenHandler({
  setLoading,
  setError,
  setConfirmationMessage,
}: UseAuthTokenHandlerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const confirmEmail = async () => {
      // PRIORITY 1: Check for hash-based tokens (Supabase implicit flow)
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        setLoading(true);
        console.log('=== Hash-Based Token Detected (Implicit Flow) ===');

        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const tokenType = hashParams.get('type');

        console.log('Token type from hash:', tokenType);

        try {
          let session, user;

          const { data: existingSessionData } = await supabase.auth.getSession();

          if (existingSessionData?.session?.user) {
            console.log('✅ Supabase auto-created session from hash');
            session = existingSessionData.session;
            user = session.user;
          } else if (accessToken && refreshToken) {
            console.log('Setting session manually from hash tokens...');

            const { data: sessionData, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (setSessionError) {
              console.error('❌ Error setting session:', setSessionError);
              if (setSessionError.message?.includes('Invalid') || setSessionError.status === 401) {
                setError('The authentication link is invalid or has expired. Please try again or contact support.');
              } else {
                setError('Failed to authenticate. Please try again.');
              }
              setLoading(false);
              window.history.replaceState(null, '', window.location.pathname);
              return;
            }

            if (!sessionData?.session?.user) {
              setError('Failed to authenticate. Please try again.');
              setLoading(false);
              window.history.replaceState(null, '', window.location.pathname);
              return;
            }

            session = sessionData.session;
            user = session.user;
          } else {
            setError('Failed to authenticate. Please try again.');
            setLoading(false);
            window.history.replaceState(null, '', window.location.pathname);
            return;
          }

          const currentStoreUser = useAuthStore.getState().user;
          if (currentStoreUser && currentStoreUser.id !== user.id) {
            console.log('⚠️ Different user in store, will be replaced during onboarding');
          }

          // Exchange Supabase token for custom JWT to use with our API
          if (session.access_token) {
            const exchanged = await exchangeSupabaseTokenForCustomJWT(session.access_token);
            if (!exchanged) {
              // Fallback: store Supabase token anyway so profile creation can work
              console.log('⚠️ Token exchange failed, using Supabase token as fallback');
              apiService.storeToken(session.access_token);
            }
          }

          const invitationCodeFromMetadata = user.user_metadata?.invitation_code;
          const invitedEmail = user.user_metadata?.invited_email || user.email;

          try {
            await apiService.createProfile({
              user_id: user.id,
              email: user.email || '',
              full_name: null,
            });
          } catch {
            console.log('Profile creation skipped (may already exist)');
          }

          const params = new URLSearchParams();
          if (invitationCodeFromMetadata) {
            params.append('invitation_code', invitationCodeFromMetadata);
          }
          if (invitedEmail) {
            params.append('email', invitedEmail);
          }
          if (tokenType === 'signup') {
            params.append('password_set', 'true');
          }

          window.history.replaceState(null, '', window.location.pathname);
          navigate(`/finish-profile?${params.toString()}`, { replace: true });
          return;
        } catch (err) {
          console.error('❌ Error processing hash-based token:', err);
          setError('Failed to verify authentication. Please try again.');
          setLoading(false);
          window.history.replaceState(null, '', window.location.pathname);
        }

        setLoading(false);
        return;
      }

      // PRIORITY 2: Check for query param-based tokens (PKCE flow)
      const magicLinkToken = searchParams.get('token');
      const tokenType = searchParams.get('type');

      if (magicLinkToken && tokenType) {
        setLoading(true);
        console.log('=== Token Detected (Magic Link or Invitation) ===');

        try {
          await supabase.auth.signOut();
          const { signOut: authStoreSignOut } = useAuthStore.getState();
          await authStoreSignOut();
        } catch {
          console.error('⚠️ Error clearing session');
        }

        try {
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: magicLinkToken,
            type: tokenType as any
          });

          if (verifyError) {
            setError(`Verification failed: ${verifyError.message}`);
            setLoading(false);
            searchParams.delete('token');
            searchParams.delete('type');
            setSearchParams(searchParams);
            return;
          }

          if (data.user && data.session) {
            // Exchange Supabase token for custom JWT to use with our API
            if (data.session.access_token) {
              const exchanged = await exchangeSupabaseTokenForCustomJWT(data.session.access_token);
              if (!exchanged) {
                // Fallback: store Supabase token anyway so profile creation can work
                console.log('⚠️ Token exchange failed, using Supabase token as fallback');
                apiService.storeToken(data.session.access_token);
              }
            }

            const invitationCodeFromMetadata = data.user.user_metadata?.invitation_code;
            const invitedEmail = data.user.user_metadata?.invited_email || data.user.email;

            try {
              await apiService.createProfile({
                user_id: data.user.id,
                email: data.user.email || '',
                full_name: null,
              });
            } catch {
              console.log('Profile creation skipped');
            }

            const params = new URLSearchParams();
            if (invitationCodeFromMetadata) {
              params.append('invitation_code', invitationCodeFromMetadata);
            }
            if (invitedEmail) {
              params.append('email', invitedEmail);
            }

            searchParams.delete('token');
            searchParams.delete('type');
            setSearchParams(searchParams);

            navigate(`/finish-profile?${params.toString()}`);
            return;
          }
        } catch (err) {
          console.error('❌ Error processing magic link:', err);
          setError('Failed to verify magic link. Please try again.');
          setLoading(false);
        }

        setLoading(false);
        return;
      }

      // Handle legacy confirmation_token
      const confirmationToken = searchParams.get('confirmation_token');

      if (confirmationToken) {
        setLoading(true);

        try {
          await supabase.auth.signOut();
          const { signOut: authStoreSignOut } = useAuthStore.getState();
          await authStoreSignOut();
        } catch {
          console.error('Error clearing existing session');
        }

        try {
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: confirmationToken,
            type: 'email'
          });

          if (verifyError) {
            setError(`Email confirmation failed: ${verifyError.message}`);
            setLoading(false);
            searchParams.delete('confirmation_token');
            setSearchParams(searchParams);
            return;
          }

          let invitationCode = searchParams.get('invitation_code');
          if (!invitationCode && data.user?.user_metadata?.invitation_code) {
            invitationCode = data.user.user_metadata.invitation_code;
          }

          if (data.user) {
            // Exchange Supabase token for custom JWT to use with our API
            if (data.session?.access_token) {
              const exchanged = await exchangeSupabaseTokenForCustomJWT(data.session.access_token);
              if (!exchanged) {
                // Fallback: store Supabase token anyway so profile creation can work
                console.log('⚠️ Token exchange failed, using Supabase token as fallback');
                apiService.storeToken(data.session.access_token);
              }
            }

            try {
              const profileResponse = await apiService.createProfile({
                user_id: data.user.id,
                email: data.user.email || '',
                full_name: null,
              });

              if (profileResponse.success || profileResponse.error?.includes('already exists')) {
                searchParams.delete('confirmation_token');
                setSearchParams(searchParams);
                const params = new URLSearchParams();
                if (invitationCode) params.append('invitation_code', invitationCode);
                if (data.user.email) params.append('email', data.user.email);
                navigate(`/finish-profile?${params.toString()}`);
                return;
              }
            } catch {
              console.error('Error creating profile');
            }
          }

          setConfirmationMessage('Email confirmed successfully! You can now log in.');
          setError('');
          searchParams.delete('confirmation_token');
          setSearchParams(searchParams);
          setLoading(false);

        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unexpected error during confirmation';
          setError(errorMessage);
          setLoading(false);
          searchParams.delete('confirmation_token');
          setSearchParams(searchParams);
        }
      }
    };

    confirmEmail();
  }, [searchParams, setSearchParams, navigate, setLoading, setError, setConfirmationMessage]);
}

